import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Brain, BarChart3, Zap, Shield, Eye, Target, TrendingUp, CheckCircle, Cpu, MessageSquare } from 'lucide-react';
import { GlowCard } from '../components/GlowCard';
import { growthSystems } from '../data/servicesData';

const capabilities = [
  {
    title: 'Gerçek Zamanlı Analiz',
    description: 'Kullanıcı davranışlarını anlık olarak analiz eder, scroll, tıklama ve terk noktalarını haritalandırır.',
    icon: Eye,
  },
  {
    title: 'Tahminsel Modelleme',
    description: 'İçerik performansını, churn riskini ve dönüşüm olasılığını önceden tahmin eder.',
    icon: TrendingUp,
  },
  {
    title: 'Otomatik Optimizasyon',
    description: 'A/B test önerileri, segment bazlı varyantlar ve aksiyon listeleri otomatik üretir.',
    icon: Zap,
  },
  {
    title: 'Anomali Tespiti',
    description: 'Performans sapmalarını ve darboğazları erken tespit eder, proaktif uyarı gönderir.',
    icon: Shield,
  },
];

const integrationPoints = [
  {
    system: 'Dijital Deneyim & Algı Mimarisi',
    features: ['Davranış analizi', 'CTA varyant önerileri', 'Dönüşüm darboğazı tespiti'],
    color: 'violet',
  },
  {
    system: 'Otorite & Talep Motoru',
    features: ['Konu kümesi analizi', 'İçerik performans skoru', 'SERP niyet eşleştirme'],
    color: 'emerald',
  },
  {
    system: 'Performans & İkna Hızlandırıcı',
    features: ['A/B test önerileri', 'Bütçe anomali uyarısı', 'Huni sürtünme tespiti'],
    color: 'pink',
  },
  {
    system: 'Veri Zekası & Sadakat',
    features: ['Churn tahmini', 'Segment bazlı öneriler', 'Kohort analizi'],
    color: 'amber',
  },
  {
    system: 'Kurumsal Entegrasyon',
    features: ['Süreç otomasyonu', 'Karar destek', 'Performans izleme'],
    color: 'cyan',
  },
];

const colorMap = {
  violet: 'border-violet-500/30 bg-violet-500/5',
  emerald: 'border-emerald-500/30 bg-emerald-500/5',
  pink: 'border-pink-500/30 bg-pink-500/5',
  amber: 'border-amber-500/30 bg-amber-500/5',
  cyan: 'border-cyan-500/30 bg-cyan-500/5',
};

export default function AifabricPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32" data-testid="aifabric-page">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden min-h-[85vh] flex items-center">
        {/* AI Brain Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/assets/ai-brain.png" 
            alt="" 
            className="absolute right-0 top-1/2 -translate-y-1/2 h-[100%] w-auto object-contain opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#0A0A0A] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
        </div>
        
        <div className="container-wide relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <span className="px-4 py-1 text-xs font-mono bg-cyan-500/20 text-cyan-400 rounded-full border border-cyan-500/30">
                  AI-POWERED
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                aifabric
              </h1>
              <p className="text-xl text-cyan-400 font-medium mb-4">
                Tüm Büyüme Sistemlerinin Ortak Zeka Katmanı
              </p>
              <p className="text-lg text-zinc-400 leading-relaxed mb-8">
                aifabric, veriyi içgörüye, içgörüyü aksiyona dönüştüren yapay zeka platformudur. 
                Her büyüme sisteminin içine entegre edilmiş olarak, manuel analiz yerine 
                gerçek zamanlı optimizasyon sağlar.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/iletisim" 
                  className="btn-gradient flex items-center gap-2"
                >
                  Demo Talep Et
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/buyume-sistemleri" 
                  className="btn-glow"
                >
                  Sistemleri Gör
                </Link>
              </div>
            </motion.div>

            {/* Right side - Empty for background image */}
            <div className="hidden lg:block" />
          </div>
        </div>
      </section>

      {/* Core Value */}
      <section className="py-24 bg-[#0A0A0A] relative overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
        </div>
        
        <div className="container-wide max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-cyan-400/80 tracking-[0.3em] uppercase mb-4 block">
              Süreç
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Karmaşıklığı Netliğe Dönüştürüyoruz
            </h2>
          </motion.div>

          {/* Flow Steps */}
          <div className="relative">
            {/* Connecting Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-zinc-700/50 to-transparent -translate-y-1/2 z-0" />
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              {[
                { 
                  step: '01', 
                  title: 'Veri Toplama', 
                  desc: 'Tüm kanallardan akan ham veriler tek bir noktada birleşir.',
                  gradient: 'from-cyan-500/20 to-cyan-600/5'
                },
                { 
                  step: '02', 
                  title: 'AI Analizi', 
                  desc: 'Yapay zeka verilerdeki gizli kalıpları ve fırsatları tespit eder.',
                  gradient: 'from-violet-500/20 to-violet-600/5'
                },
                { 
                  step: '03', 
                  title: 'Aksiyon Planı', 
                  desc: '"Şimdi ne yapmalıyım?" sorusuna net ve öncelikli cevaplar.',
                  gradient: 'from-emerald-500/20 to-emerald-600/5'
                },
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="relative group"
                >
                  <div className={`relative p-8 rounded-2xl bg-gradient-to-b ${item.gradient} border border-zinc-800/50 backdrop-blur-sm transition-all duration-300 group-hover:border-zinc-700/50`}>
                    {/* Step Number */}
                    <div className="absolute -top-4 left-8">
                      <span className="inline-flex items-center justify-center w-8 h-8 text-xs font-mono font-bold text-white bg-zinc-900 border border-zinc-700 rounded-full">
                        {item.step}
                      </span>
                    </div>
                    
                    <div className="pt-4">
                      <h3 className="text-xl font-semibold text-white mb-3">
                        {item.title}
                      </h3>
                      <p className="text-zinc-400 text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                  
                  {/* Arrow for desktop */}
                  {index < 2 && (
                    <div className="hidden lg:flex absolute -right-6 top-1/2 -translate-y-1/2 z-10 w-12 items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center">
                        <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-cyan-400 tracking-widest uppercase mb-4 block">
              Yetenekler
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              aifabric Ne Yapar?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <GlowCard className="p-8 h-full text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-6">
                    <capability.icon className="w-8 h-8 text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{capability.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{capability.description}</p>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* System Integrations - Central Hub Orbit Design */}
      <section className="section-padding bg-[#07070d] relative overflow-hidden min-h-[900px]">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-violet-500/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-3xl" />
        </div>
        
        <div className="container-wide max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Her Sistemde aifabric
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Tüm büyüme sistemlerine entegre edilmiş yapay zeka katmanı
            </p>
          </motion.div>

          {/* Orbit Container */}
          <div className="relative h-[850px] flex items-center justify-center">
            
            {/* Orbit Rings */}
            <div className="absolute w-[350px] h-[350px] rounded-full border border-zinc-800/50" />
            <div className="absolute w-[550px] h-[550px] rounded-full border border-zinc-800/30 border-dashed" />
            <div className="absolute w-[800px] h-[800px] rounded-full border border-zinc-800/20" />
            
            {/* Animated orbit ring */}
            <motion.div 
              className="absolute w-[550px] h-[550px] rounded-full border-2 border-transparent"
              style={{ 
                background: 'linear-gradient(90deg, transparent 0%, rgba(139,92,246,0.3) 50%, transparent 100%)',
                backgroundSize: '200% 100%',
              }}
              animate={{ 
                rotate: 360,
              }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            />

            {/* Central Hub - aifabric Logo */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, type: "spring" }}
              className="relative z-20"
            >
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute inset-[-20px] bg-gradient-to-br from-violet-500 to-cyan-500 rounded-full blur-3xl opacity-30" />
                
                {/* Logo */}
                <div className="relative w-40 h-40 flex items-center justify-center">
                  <img 
                    src="/assets/aifabric-logo.png" 
                    alt="aifabric" 
                    className="w-36 h-auto"
                  />
                </div>
                
                {/* Pulse rings */}
                <motion.div
                  className="absolute inset-0 w-40 h-40 rounded-full border border-violet-500/50"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <motion.div
                  className="absolute inset-0 w-40 h-40 rounded-full border border-cyan-500/50"
                  animate={{ scale: [1, 1.8, 1], opacity: [0.3, 0, 0.3] }}
                  transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
                />
              </div>
            </motion.div>

            {/* Orbiting Systems */}
            {[
              { 
                name: 'Dijital Deneyim', 
                desc: 'Ziyaretçilerinizin sitede ne yaptığını görün, nerede takıldıklarını keşfedin',
                icon: Eye, 
                color: 'violet',
                angle: 0,
                radius: 300,
                delay: 0
              },
              { 
                name: 'Otorite Motoru', 
                desc: 'Google\'da üst sıralara çıkın, doğru içeriklerle müşteri çekin',
                icon: TrendingUp, 
                color: 'emerald',
                angle: 60,
                radius: 320,
                delay: 0.1
              },
              { 
                name: 'Veri Güvenliği', 
                desc: 'Müşteri verileriniz şifreli ve güvende, KVKK uyumlu altyapı',
                icon: Shield, 
                color: 'amber',
                angle: 120,
                radius: 300,
                delay: 0.2
              },
              { 
                name: 'Performans Analizi', 
                desc: 'Hangi reklam çalışıyor, hangi sayfa satıyor? Anlık raporlar',
                icon: BarChart3, 
                color: 'pink',
                angle: 180,
                radius: 320,
                delay: 0.3
              },
              { 
                name: 'Akıllı Optimizasyon', 
                desc: 'AI size "şunu yap, bunu değiştir" diyor, siz uyguluyorsunuz',
                icon: Zap, 
                color: 'cyan',
                angle: 240,
                radius: 300,
                delay: 0.4
              },
              { 
                name: 'Sistem Entegrasyonu', 
                desc: 'Tüm araçlarınız tek panelde: CRM, e-posta, sosyal medya',
                icon: Cpu, 
                color: 'fuchsia',
                angle: 300,
                radius: 320,
                delay: 0.5
              },
            ].map((system, index) => {
              const angleRad = (system.angle * Math.PI) / 180;
              const x = Math.cos(angleRad) * system.radius;
              const y = Math.sin(angleRad) * system.radius;
              
              const colorClasses = {
                violet: { bg: 'from-violet-500/20 to-violet-600/10', border: 'border-violet-500/40', icon: 'text-violet-400', glow: 'bg-violet-500/20', line: 'from-violet-500/60' },
                emerald: { bg: 'from-emerald-500/20 to-emerald-600/10', border: 'border-emerald-500/40', icon: 'text-emerald-400', glow: 'bg-emerald-500/20', line: 'from-emerald-500/60' },
                amber: { bg: 'from-amber-500/20 to-amber-600/10', border: 'border-amber-500/40', icon: 'text-amber-400', glow: 'bg-amber-500/20', line: 'from-amber-500/60' },
                pink: { bg: 'from-pink-500/20 to-pink-600/10', border: 'border-pink-500/40', icon: 'text-pink-400', glow: 'bg-pink-500/20', line: 'from-pink-500/60' },
                cyan: { bg: 'from-cyan-500/20 to-cyan-600/10', border: 'border-cyan-500/40', icon: 'text-cyan-400', glow: 'bg-cyan-500/20', line: 'from-cyan-500/60' },
                fuchsia: { bg: 'from-fuchsia-500/20 to-fuchsia-600/10', border: 'border-fuchsia-500/40', icon: 'text-fuchsia-400', glow: 'bg-fuchsia-500/20', line: 'from-fuchsia-500/60' },
              };
              
              const colors = colorClasses[system.color];
              
              return (
                <motion.div
                  key={system.name}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: system.delay, type: "spring" }}
                  className="absolute z-10"
                  style={{ 
                    left: `calc(50% + ${x}px - 100px)`,
                    top: `calc(50% + ${y}px - 55px)`,
                  }}
                >
                  {/* Connection line to center */}
                  <svg 
                    className="absolute pointer-events-none" 
                    style={{
                      width: Math.abs(x) + 100,
                      height: Math.abs(y) + 55,
                      left: x < 0 ? 'auto' : -Math.abs(x),
                      right: x < 0 ? -Math.abs(x) : 'auto',
                      top: y < 0 ? 'auto' : -Math.abs(y),
                      bottom: y < 0 ? -Math.abs(y) : 'auto',
                    }}
                  >
                    <line 
                      x1={x < 0 ? Math.abs(x) + 100 : 100}
                      y1={y < 0 ? Math.abs(y) + 55 : 55}
                      x2={x < 0 ? 0 : Math.abs(x) + 100}
                      y2={y < 0 ? 0 : Math.abs(y) + 55}
                      stroke="url(#lineGradient)"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                      className="opacity-40"
                    />
                  </svg>
                  
                  <Link to="/buyume-sistemleri" className="group block">
                    <div className={`relative w-[200px] p-5 rounded-2xl bg-gradient-to-br ${colors.bg} border ${colors.border} backdrop-blur-sm hover:scale-105 transition-all duration-300`}>
                      {/* Glow */}
                      <div className={`absolute -inset-1 ${colors.glow} rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity`} />
                      
                      <div className="relative">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors.bg} border ${colors.border} flex items-center justify-center mb-3`}>
                          <system.icon className={`w-5 h-5 ${colors.icon}`} />
                        </div>
                        <h4 className="text-base font-bold text-white mb-2">{system.name}</h4>
                        <p className="text-sm text-zinc-400 leading-relaxed">{system.desc}</p>
                      </div>
                      
                      {/* Pulse dot */}
                      <motion.div
                        className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${colors.glow}`}
                        animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                        transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                      />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
            
            {/* SVG Gradients */}
            <svg className="absolute w-0 h-0">
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.2" />
                </linearGradient>
              </defs>
            </svg>
            
            {/* Floating particles */}
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-violet-400/40"
                style={{
                  left: `${20 + Math.random() * 60}%`,
                  top: `${20 + Math.random() * 60}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  opacity: [0.2, 0.6, 0.2],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono text-emerald-400 tracking-widest uppercase mb-4 block">
              Nasıl Çalışır
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              3 Adımda aifabric
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Veri Toplama',
                description: 'Web siteniz, reklamlarınız ve CRM verileriniz aifabric\'e bağlanır.',
                icon: BarChart3,
              },
              {
                step: '02',
                title: 'AI Analiz',
                description: 'Yapay zeka, verileri analiz eder ve kalıpları, fırsatları tespit eder.',
                icon: Brain,
              },
              {
                step: '03',
                title: 'Aksiyon Önerileri',
                description: 'Net, önceliklendirilmiş aksiyon listeleri ve optimizasyon önerileri alırsınız.',
                icon: Target,
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
              >
                <div className="text-8xl font-bold text-zinc-900 absolute -top-4 -left-2">{item.step}</div>
                <GlowCard className="p-8 relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-6">
                    <item.icon className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{item.description}</p>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Features */}
      <section className="section-padding bg-[#121212]">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-xs font-mono text-pink-400 tracking-widest uppercase mb-4 block">
                Kurumsal Kullanım
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Sadece Pazarlama Değil
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                aifabric, pazarlama dışında satış, destek ve operasyon süreçlerinizde de 
                yapay zeka gücünü kullanmanızı sağlar.
              </p>
              <ul className="space-y-4">
                {[
                  'Satış ekibi için AI destekli sunum ve teklif önerileri',
                  'Müşteri destek için otomatik cevap taslakları',
                  'Operasyon için süreç otomasyon önerileri',
                  'Yönetim için karar destek panelleri'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/buyume-sistemleri/aifabric-kurumsal-entegrasyon"
                className="inline-flex items-center gap-2 mt-8 text-pink-400 hover:text-pink-300 transition-colors"
              >
                Kurumsal Entegrasyon Detayları
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <GlowCard className="p-8">
                <div className="space-y-6">
                  {[
                    { icon: Cpu, title: 'AI Use-Case Workshop', desc: 'İş süreçlerinize özel AI fırsatları' },
                    { icon: MessageSquare, title: 'Hipnotik Satış Eğitimi', desc: 'Taner Torun imzalı workshop' },
                    { icon: BarChart3, title: 'Karar Destek Panelleri', desc: 'Gerçek zamanlı iş zekası' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-zinc-900/50 rounded-xl">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-rose-500/20 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-6 h-6 text-pink-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium mb-1">{item.title}</h4>
                        <p className="text-zinc-500 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </GlowCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="relative z-10">
              {/* aifabric Logo */}
              <div className="mb-4">
                <img 
                  src="/assets/aifabric-logo.png" 
                  alt="aifabric" 
                  className="h-10 md:h-12 w-auto mx-auto"
                />
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Deneyimleyin
              </h2>
              
              <p className="text-zinc-400 text-lg mb-10">
                Verilerinizi içgörüye, içgörüleri aksiyona dönüştürmeye hazır mısınız? 
                15 dakikalık demo görüşmesinde aifabric'in sizin için neler yapabileceğini görelim.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link 
                  to="/iletisim" 
                  className="btn-gradient flex items-center gap-2"
                  data-testid="aifabric-cta-primary"
                >
                  Demo Talep Et
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/buyume-sistemleri" 
                  className="btn-glow"
                  data-testid="aifabric-cta-secondary"
                >
                  Büyüme Sistemlerini Gör
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
